import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  BehaviorSubject,
  Observable,
  forkJoin,
} from 'rxjs';
import { Market } from 'src/app/core/models/market.model';
import { HttpClient } from '@angular/common/http';
import * as camelcaseKeys from 'camelcase-keys';
import { HighlightedMarket } from 'src/app/core/models/highlighted-market.model';
import { Currency, CurrencyDetails } from 'src/app/core/models/currency.model';
import { Faq } from 'src/app/core/models/faq.model';
import { PageContent } from 'src/app/core/models/page-content.model';
import { BreakpointObserver } from '@angular/cdk/layout';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  highlightedMarkets: BehaviorSubject<HighlightedMarket | null> =
    new BehaviorSubject<HighlightedMarket | null>(null);

  isDesktop$ = this.breakpointObserver
    .observe('(min-width: 991px)')
    .pipe(map((v) => v.matches));

  constructor(
    private http: HttpClient,
    private breakpointObserver: BreakpointObserver
  ) {
  }

  private markets$ = new BehaviorSubject<{
    irrMarkets: Market[];
    usdtMarkets: Market[];
  }>({ irrMarkets: [], usdtMarkets: [] });

  getCurrencies(): Observable<CurrencyDetails[]> {
    return this.http.get('/v1/currencies').pipe(
      map((response: any) => response.data),
      map((data: any[]) =>
        data.map((each) => camelcaseKeys(each, { deep: true }))
      )
    );
  }

  getInitialMarkets() {
    forkJoin([
      this.requestMarketList('IRR'),
      this.requestMarketList('USDT'),
    ]).subscribe(([irrMarkets, usdtMarkets]) => {
      this.markets$.next({ irrMarkets, usdtMarkets });
    });
  }

  private requestMarketList(
    quoteCurrency: 'IRR' | 'USDT'
  ): Observable<Market[]> {
    return this.http.get(`/v4/currency/history/${quoteCurrency}`).pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map((each) => camelcaseKeys(each)))
    );
  }

  getMarketList(quoteCurrency: 'IRR' | 'USDT'): Observable<Market[]> {
    return this.markets$.pipe(
      map((data) => data[quoteCurrency === 'IRR' ? 'irrMarkets' : 'usdtMarkets']),
      map(v => JSON.parse(JSON.stringify(v)))
    );
  }

  getPlatformSummaryData(): Observable<{ users: number; orders: number }> {
    return this.http
      .get('/v3/landing/statics-counter')
      .pipe(map((response: any) => response.data));
  }

  getScoreboard(
    type: 'daily' | 'weekly' | 'monthly' | 'tournament'
  ): Observable<any> {
    return this.http.get(`/v1/profit/scoreboard/${type}`).pipe(
      map((response: any) => response.data),
      map((data: any) => data.scoreboard)
    );
  }

  getCurrency(id: string | null): Observable<Currency> {
    return this.http.get(`/v3/currency/${id}`).pipe(
      map((response: any) => response.data),
      map((data: any) => camelcaseKeys(data, { deep: true }))
    );
  }

  getCurrencyFaqList(id: string): Observable<Faq[]> {
    return this.http
      .request('get', '/v1/currency/faqs/' + id)
      .pipe(map((response: any) => response.data));
  }

  getPageContent(id: string | null): Observable<PageContent> {
    return this.http
      .get(`/v1/landing/content/${id}`)
      .pipe(map((response: any) => response.data));
  }
}
